import { useCallback, useEffect, useState } from 'react'
import { api } from '../services/api'

/**
 * Whether a household has a card on file at a specific facility.
 *
 * Facilities can run on separate Stripe instances, so a client's saved card belongs to the
 * facility it was added at. A client with a card at one location has nothing on file at
 * another, and a visit booked there could not be charged.
 */
const useFacilityPaymentStatus = (household_id, facility_id, { ensureCustomer = false } = {}) => {
	const [loading, setLoading] = useState(!!(household_id && facility_id))
	const [hasPaymentMethod, setHasPaymentMethod] = useState(null)
	const [facilityName, setFacilityName] = useState('')
	const [paymentMethods, setPaymentMethods] = useState([])

	const fetchStatus = useCallback(async () => {
		if (!household_id || !facility_id) {
			setHasPaymentMethod(null)
			setPaymentMethods([])

			return
		}

		setLoading(true)

		try {
			const { data } = await api.get(`/clients/${household_id}/facilities/${facility_id}/paymentStatus`, {
				params: ensureCustomer ? { ensure_customer: 'true' } : undefined
			})

			setHasPaymentMethod(!!data?.has_payment_method)
			setFacilityName(data?.facility_name || '')
			setPaymentMethods(data?.payment_methods || [])
		} catch (e) {
			// Leave as unknown so the UI shows nothing rather than a false alarm.
			setHasPaymentMethod(null)
			setPaymentMethods([])
		} finally {
			setLoading(false)
		}
	}, [household_id, facility_id, ensureCustomer])

	useEffect(() => {
		fetchStatus()
	}, [fetchStatus])

	return {
		loading,
		hasPaymentMethod,
		facilityName,
		paymentMethods,
		refresh: fetchStatus
	}
}

export default useFacilityPaymentStatus
